/**
 * Sense → Today. Slice 62, the pillar's landing page.
 *
 * The fetch, the frame, and what the buttons do. What is drawn, and why, is
 * `TodayContent.tsx`'s.
 *
 * ## ORDER OF OPERATIONS WHEN AN ACTION IS TAKEN
 *
 * A link: record "acted on", say so, then follow it. A save: save first, and
 * only if the save succeeded record the decision and go on to a campaign form
 * for the new segment — recording first would put a decision in the ledger for
 * an audience that was never saved (the Overview's own refusals, such as "the
 * population is now empty", still apply). A record that fails AFTER a save says
 * the segment was saved, because it was. A flow draft (slice 70) follows the
 * save's order exactly: `POST /flows` creates a DRAFT from the finding's
 * template and segment, only then is the decision recorded, and the person
 * lands in the flow editor at `/flows/:id` — never on a launch, which stays
 * the editor's own button behind its preflight. Recording first would put a
 * decision in the ledger for a flow that was never drafted; the earlier cut
 * navigated to `/flows/new` and created nothing, which left "Draft a flow"
 * drafting nothing. A record that fails on a link — the
 * finding has gone since the page loaded — says so and reloads, rather than
 * following a link about something that is no longer true.
 *
 * Slice 65 gave every Sense screen the same band at the top: the eyebrow
 * "Sense" with the pillar's glyph, and one line on what this screen answers.
 * Since the split into Reach, Connect and Sense, a person arriving here from a
 * link on the Overview or a campaign page needs to be told which part of Wend
 * they are in; the sidebar says it too, but only to someone who looks there.
 * The band sits here in the Page wrapper and not in the Content component
 * that `verify-sense-screens` renders, so those renders stay the screen's
 * substance and do not start counting a header's words. The name and glyph
 * come from `lib/pillars.tsx`, the one place a pillar is named.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Finding } from '@wend/shared';
import { useToast, PageHeader } from '@wend/ui';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import { errorMessage } from '../../lib/api/errors';
import { saveOpportunityCohort } from '../../lib/api/overview';
import { draftFlowForFinding, fetchToday, recordSenseAction } from '../../lib/api/sense';
import { PILLAR_IDENTITY } from '../../lib/pillars';
import { useAsyncData } from '../../lib/use-async-data';
import { TodayContent } from './TodayContent';

export function TodayPage() {
  const { data, error, status, reload } = useAsyncData((signal) => fetchToday(signal), []);
  const [busy, setBusy] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  async function take(finding: Finding): Promise<void> {
    setBusy(finding.fingerprint);
    try {
      if (finding.action.kind === 'save') {
        const saved = await saveOpportunityCohort(finding.action.opportunity);
        try {
          await recordSenseAction({ fingerprint: finding.fingerprint, decision: 'taken' });
          toast({ tone: 'success', title: `Saved as “${saved.segment.name}”`, description: 'Off Today for 30 days — it is in the action ledger.' });
        } catch {
          toast({ tone: 'success', title: `Saved as “${saved.segment.name}”`, description: 'The finding had already left the queue.' });
        }
        navigate(`/campaigns/new?segment=${encodeURIComponent(saved.segment.id)}`);
      } else if (finding.action.kind === 'flow_draft') {
        // Slice 70: create the draft, then record, then open it — the save's order.
        const flow = await draftFlowForFinding(finding.action.template, finding.action.segmentId);
        try {
          await recordSenseAction({ fingerprint: finding.fingerprint, decision: 'taken' });
          toast({ tone: 'success', title: `Drafted “${flow.name}”`, description: 'Nothing is launched — check it and launch it yourself. Off Today for 30 days.' });
        } catch {
          toast({ tone: 'success', title: `Drafted “${flow.name}”`, description: 'Nothing is launched. The finding had already left the queue.' });
        }
        navigate(`/flows/${encodeURIComponent(flow.id)}`);
      } else {
        await recordSenseAction({ fingerprint: finding.fingerprint, decision: 'taken' });
        toast({ tone: 'success', title: 'Off Today for 30 days', description: 'It is in the action ledger.' });
        navigate(finding.action.href);
      }
    } catch (cause) {
      toast({ tone: 'error', title: 'That did not go through', description: errorMessage(cause) });
      reload();
    } finally {
      setBusy(null);
    }
  }

  async function dismiss(finding: Finding): Promise<void> {
    setBusy(finding.fingerprint);
    try {
      await recordSenseAction({ fingerprint: finding.fingerprint, decision: 'dismissed' });
      toast({ tone: 'success', title: 'Off the queue for 30 days', description: 'It is in the action ledger.' });
    } catch (cause) {
      toast({ tone: 'error', title: 'That did not go through', description: errorMessage(cause) });
    } finally {
      setBusy(null);
      reload();
    }
  }

  return (
    <AppLayout title="Today">
      <PageHeader
        eyebrow={PILLAR_IDENTITY.sense.label}
        icon={PILLAR_IDENTITY.sense.icon}
        lede="The few things worth doing, most valuable first, each with one action."
      />
      <div className="flex flex-col gap-6" data-role="today-page">
        {status === 'error' && data === null && (
          <ErrorState
            error={error}
            title="Could not load the queue"
            onRetry={reload}
            className="flex flex-col items-start gap-3 rounded-base border border-line px-4 py-5"
          />
        )}
        {data !== null && (
          <TodayContent
            today={data}
            handlers={{ onTake: (one) => void take(one), onDismiss: (one) => void dismiss(one), busy }}
          />
        )}
      </div>
    </AppLayout>
  );
}
